import { useEffect, useRef, useState } from "react";
import { useT } from "../i18n";

/**
 * TitleInput is the editing half on its own: commit, cancel, and the save call.
 *
 * It is separate from EditableTitle because a row whose whole surface is a link
 * cannot also use its title as a rename trigger — the two clicks fight. Those
 * rows open the input from their own action button and render this directly,
 * while page headings use EditableTitle below and get the pencil for free.
 *
 * Saving on blur as well as on Enter is deliberate: on a phone there is no
 * Enter key in view once the keyboard is up, and tapping away is how people
 * finish. Escape restores the original, so abandoning an edit does not mean
 * retyping the old name.
 */
export function TitleInput({
  value,
  onSave,
  onDone,
  label = "name",
  className,
  testId,
}: {
  value: string;
  onSave: (next: string) => Promise<unknown>;
  onDone: () => void;
  label?: string;
  className?: string;
  testId?: string;
}) {
  const [draft, setDraft] = useState(value);
  const t = useT();
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const input = useRef<HTMLInputElement | null>(null);
  // Escape unmounts the field, which fires blur on the way out. Without this
  // the cancel would immediately be followed by a save of the abandoned draft.
  const abandoned = useRef(false);

  useEffect(() => {
    input.current?.focus();
    input.current?.select();
  }, []);

  async function commit() {
    const next = draft.trim();
    if (!next || next === value) {
      onDone();
      return;
    }
    setSaving(true);
    try {
      await onSave(next);
      setError(null);
      onDone();
    } catch (err) {
      // Stay open on failure. Closing would discard what they typed and leave
      // them to work out what the old name was.
      setError(err instanceof Error ? err.message : t.title.renameFailed);
    } finally {
      setSaving(false);
    }
  }

  return (
    <span className="flex min-w-0 flex-col gap-1">
      <input
        ref={input}
        dir="auto"
        value={draft}
        disabled={saving}
        aria-label={t.title.edit(label)}
        data-testid={testId ?? "title-input"}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          if (abandoned.current) {
            abandoned.current = false;
            return;
          }
          void commit();
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            void commit();
          }
          if (e.key === "Escape") {
            abandoned.current = true;
            setError(null);
            onDone();
          }
        }}
        className={`w-full rounded-control border border-sage bg-surface px-2 py-1 disabled:opacity-60 ${className ?? ""}`}
      />
      {error && (
        <span
          role="alert"
          data-testid="rename-error"
          className="text-sm text-red-600 dark:text-red-400"
        >
          {error}
        </span>
      )}
    </span>
  );
}

/**
 * EditableTitle turns a heading into a text field in place.
 *
 * In place rather than in a dialog: renaming is a correction, not a decision,
 * and a modal makes fixing a two-character typo feel like a commitment.
 */
export default function EditableTitle({
  value,
  onSave,
  canEdit,
  className,
  as: Tag = "h1",
  label = "name",
  testId,
}: {
  value: string;
  onSave: (next: string) => Promise<unknown>;
  canEdit: boolean;
  className?: string;
  as?: "h1" | "h2";
  label?: string;
  testId?: string;
}) {
  const [editing, setEditing] = useState(false);
  const t = useT();

  if (!canEdit) {
    return (
      <Tag dir="auto" className={className} data-testid={testId}>
        {value}
      </Tag>
    );
  }

  if (editing) {
    return (
      <TitleInput
        value={value}
        onSave={onSave}
        onDone={() => setEditing(false)}
        label={label}
        className={className}
        testId={testId ? `${testId}-input` : undefined}
      />
    );
  }

  return (
    <Tag dir="auto" className={className} data-testid={testId}>
      <button
        type="button"
        onClick={() => setEditing(true)}
        data-testid={testId ? `${testId}-edit` : "rename"}
        title={t.title.rename(label)}
        className="group/rename inline-flex max-w-full items-center gap-1.5 rounded-control text-start hover:text-sage-deep"
      >
        <span className="min-w-0 break-words">{value}</span>
        {/* Revealed on hover for a pointer, and always visible on a touch
            screen, where there is no hover to reveal it with. */}
        <svg
          viewBox="0 0 24 24"
          aria-hidden="true"
          className="size-[0.62em] shrink-0 opacity-0 transition-opacity group-hover/rename:opacity-60 group-focus-visible/rename:opacity-60 [@media(hover:none)]:opacity-40"
          fill="currentColor"
        >
          <path d="M3 17.25V21h3.75L17.8 9.94l-3.75-3.75L3 17.25ZM20.7 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83Z" />
        </svg>
      </button>
    </Tag>
  );
}
